/**
 * Team Management Page
 * Shows team members with dual view (table/card) and pending invitations tab.
 * Protected route requiring ADMIN role.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTeam } from '../../hooks/useTeam';
import { useCompany } from '../../hooks/useCompany';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../components/common/ToastProvider';
import PageHeader from '../../components/common/PageHeader';
import {
  updateMemberRole,
  removeMember,
  resendInvitation,
  cancelInvitation,
} from '../../services/teamService';
import TeamList from '../../components/team/TeamList';
import TeamInvitePanel from '../../components/team/TeamInvitePanel';
import PendingInvitationsTab from '../../components/team/PendingInvitationsTab';
import RoleChangeDialog from '../../components/team/RoleChangeDialog';
import MemberRemovalDialog from '../../components/team/MemberRemovalDialog';
import type { TeamMember, TeamRole } from '../../types/company';

type TabType = 'members' | 'pending';

/**
 * Loading skeleton for team page - shimmer placeholders for 3 rows/cards.
 */
function TeamPageSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      {/* Header skeleton */}
      <div className="flex items-center justify-between mb-6">
        <div className="h-8 w-32 bg-gray-200 rounded" />
        <div className="h-10 w-36 bg-gray-200 rounded" />
      </div>
      {/* Tabs skeleton */}
      <div className="flex gap-4 border-b border-gray-200 mb-6">
        <div className="h-6 w-24 bg-gray-200 rounded" />
        <div className="h-6 w-36 bg-gray-200 rounded" />
      </div>
      {/* List skeleton - 3 items */}
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-16 bg-gray-200 rounded-lg"
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Error state component for team page.
 */
function TeamPageError({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation('common');

  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
        <i className="ri-error-warning-line text-2xl text-red-600" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        {t('error', 'Error')}
      </h3>
      <p className="text-gray-600 mb-4 text-center max-w-md">{message}</p>
      <button
        onClick={onRetry}
        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        {t('retry', 'Retry')}
      </button>
    </div>
  );
}

export default function TeamPage() {
  const { t, i18n } = useTranslation('team');
  const [activeTab, setActiveTab] = useState<TabType>('members');
  const navigate = useNavigate();
  const { members, invitations, usage, isLoading, error, refetch } = useTeam();
  const { myRole } = useCompany();
  const { session } = useAuth();
  const { showToast } = useToast();

  // Dialog states
  const [roleChangeTarget, setRoleChangeTarget] = useState<TeamMember | null>(null);
  const [removalTarget, setRemovalTarget] = useState<TeamMember | null>(null);

  // Invite panel state
  const [isInvitePanelOpen, setIsInvitePanelOpen] = useState(false);

  // Handler for invite button - checks subscription gating before opening panel
  const handleInviteClick = () => {
    if (usage && !usage.canInvite) {
      if (!usage.hasSubscription) {
        showToast(t('subscriptionRequired', 'Subscribe to invite more team members'), 'warning');
        // Defer navigation to let toast render and avoid UI freeze
        setTimeout(() => navigate('/membership?reason=required'), 100);
        return;
      }
      showToast(t('limitReached', 'Team member limit reached. Upgrade your plan.'), 'warning');
      return;
    }
    setIsInvitePanelOpen(true);
  };

  // Handler for successful invitation - refetch team data
  const handleInviteSuccess = () => {
    refetch();
  };

  // Open role change dialog
  const handleRoleChange = (member: TeamMember) => {
    setRoleChangeTarget(member);
  };

  // Open removal dialog
  const handleRemoveMember = (member: TeamMember) => {
    setRemovalTarget(member);
  };

  // Get localized role label
  const getRoleLabel = (role: TeamRole): string => {
    switch (role) {
      case 'OWNER': return t('roleOwner');
      case 'ADMIN': return t('roleAdmin');
      case 'AGENT': return t('roleAgent');
      case 'VIEWER': return t('roleViewer');
      default: return role;
    }
  };

  // Confirm role change - calls API, shows toast, refetches
  const confirmRoleChange = async (newRole: TeamRole) => {
    if (!roleChangeTarget || !session?.token) return;

    // Don't allow changing to OWNER through this flow
    if (newRole === 'OWNER') {
      throw new Error('Ownership transfer not supported in this dialog');
    }

    await updateMemberRole(session.token, roleChangeTarget.id, newRole);

    const roleLabel = getRoleLabel(newRole);
    showToast(
      t('roleChanged', { name: roleChangeTarget.displayName, role: roleLabel }),
      'success'
    );

    await refetch();
  };

  // Confirm member removal — collects the admin's reason, transfers the
  // member's listings to the company owner, and triggers the bilingual
  // removal email. The dialog enforces ≥10 chars locally; the backend
  // enforces the same and 400s if it gets less.
  const confirmRemoval = async (reason: string) => {
    if (!removalTarget || !session?.token) return;

    const result = await removeMember(session.token, removalTarget.id, reason);

    const transferred = result?.transferredCount ?? 0;
    if (transferred > 0) {
      showToast(
        t('memberRemovedWithTransfer', {
          name: removalTarget.displayName,
          count: transferred,
          defaultValue: '{{name}} removed · {{count}} listings transferred to the company',
        }),
        'success',
      );
    } else {
      showToast(
        t('memberRemoved', { name: removalTarget.displayName }),
        'success',
      );
    }

    await refetch();
  };

  // Resend invitation - calls API, shows toast, refetches
  const handleResendInvitation = async (invitationId: string) => {
    if (!session?.token) return;

    try {
      const invitation = invitations.find((inv) => inv.id === invitationId);
      await resendInvitation(session.token, invitationId);

      showToast(
        t('invitationResent', { email: invitation?.email || '' }),
        'success'
      );

      await refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('resendFailed', 'Failed to resend invitation');
      showToast(message, 'error');
    }
  };

  // Cancel invitation - calls API, shows toast, refetches
  const handleCancelInvitation = async (invitationId: string) => {
    if (!session?.token) return;

    try {
      await cancelInvitation(session.token, invitationId);

      showToast(t('invitationCancelled', 'Invitation cancelled'), 'success');

      await refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('cancelFailed', 'Failed to cancel invitation');
      showToast(message, 'error');
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white" dir={i18n.dir()}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <TeamPageSkeleton />
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-white" dir={i18n.dir()}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <TeamPageError message={error} onRetry={refetch} />
        </div>
      </div>
    );
  }

  const pendingCount = invitations.length;

  return (
    <div className="min-h-screen bg-white" dir={i18n.dir()}>
      <PageHeader
        title={t('title', 'Team')}
        backTo="/company/dashboard"
        backLabel={t('backToDashboard', 'Dashboard')}
        action={
          <button
            onClick={handleInviteClick}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
          >
            <i className="ri-user-add-line" />
            {t('inviteButton', 'Invite')}
          </button>
        }
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Team Usage Bar */}
        {usage && (
          <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                {t('teamUsage', 'Team Members')}
              </span>
              <span className="text-sm font-semibold text-gray-900">
                {usage.currentCount} / {usage.maxAllowed}
                {!usage.hasSubscription && (
                  <span className="text-xs text-gray-500 ml-1">(free tier)</span>
                )}
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                className={`h-full rounded-full transition-all duration-300 ${
                  usage.currentCount >= usage.maxAllowed ? 'bg-red-500' : 'bg-[#004D40]'
                }`}
                style={{ width: `${Math.min(100, (usage.currentCount / Math.max(1, usage.maxAllowed)) * 100)}%` }}
              />
            </div>
            {!usage.hasSubscription && usage.currentCount >= usage.maxAllowed && (
              <button
                onClick={() => navigate('/membership?reason=required')}
                className="mt-2 text-sm text-[#004D40] hover:text-[#003930] font-medium"
              >
                {t('upgradeToInviteMore', 'Subscribe to invite more members →')}
              </button>
            )}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex gap-8" aria-label="Team tabs">
            <button
              onClick={() => setActiveTab('members')}
              className={`
                pb-3 px-1 text-sm font-medium border-b-2 transition-colors
                ${
                  activeTab === 'members'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {t('members', 'Members')}
            </button>
            <button
              onClick={() => setActiveTab('pending')}
              className={`
                pb-3 px-1 text-sm font-medium border-b-2 transition-colors inline-flex items-center gap-2
                ${
                  activeTab === 'pending'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {t('pendingInvitations', 'Pending Invitations')}
              {pendingCount > 0 && (
                <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                  {pendingCount}
                </span>
              )}
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'members' ? (
          <TeamList
            members={members}
            onRoleChange={handleRoleChange}
            onRemove={handleRemoveMember}
            onInvite={handleInviteClick}
          />
        ) : (
          <PendingInvitationsTab
            invitations={invitations}
            onResend={handleResendInvitation}
            onCancel={handleCancelInvitation}
          />
        )}
      </div>

      {/* Role Change Dialog */}
      {roleChangeTarget && (
        <RoleChangeDialog
          isOpen={!!roleChangeTarget}
          onClose={() => setRoleChangeTarget(null)}
          member={roleChangeTarget}
          onConfirm={confirmRoleChange}
          currentUserRole={myRole ?? undefined}
        />
      )}

      {/* Member Removal Dialog */}
      {removalTarget && (
        <MemberRemovalDialog
          isOpen={!!removalTarget}
          onClose={() => setRemovalTarget(null)}
          member={removalTarget}
          onConfirm={confirmRemoval}
        />
      )}

      {/* Team Invite Panel */}
      <TeamInvitePanel
        isOpen={isInvitePanelOpen}
        onClose={() => setIsInvitePanelOpen(false)}
        onSuccess={handleInviteSuccess}
      />
    </div>
  );
}
